/**
 * Client-side "Add to Compare" state, synced across every button/drawer instance on the page.
 */
(function () {
  const STORAGE_KEY = 'theme-product-compare';
  const EVENT_NAME = 'theme:compare-change';
  const LIMIT_EVENT_NAME = 'theme:compare-limit-reached';
  const MAX_ITEMS = window.themeCompareMax || 4;

  function readList() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (error) {
      console.error('Unable to read compare list:', error);
      return [];
    }
  }

  function writeList(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      console.error('Unable to save compare list:', error);
    }
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { items: list } }));
  }

  window.CompareStore = {
    max: MAX_ITEMS,
    eventName: EVENT_NAME,
    limitEventName: LIMIT_EVENT_NAME,

    getAll() {
      return readList();
    },

    getIds() {
      return readList().map((item) => item.id);
    },

    has(id) {
      return readList().some((item) => item.id === Number(id));
    },

    count() {
      return readList().length;
    },

    isFull() {
      return readList().length >= MAX_ITEMS;
    },

    add(item) {
      const list = readList();
      const id = Number(item.id);

      if (list.some((entry) => entry.id === id)) {
        return { ok: true, list };
      }

      if (list.length >= MAX_ITEMS) {
        document.dispatchEvent(new CustomEvent(LIMIT_EVENT_NAME, { detail: { max: MAX_ITEMS } }));
        return { ok: false, reason: 'limit', list };
      }

      list.push({ ...item, id });
      writeList(list);
      return { ok: true, list };
    },

    remove(id) {
      const list = readList().filter((entry) => entry.id !== Number(id));
      writeList(list);
      return list;
    },

    toggle(item) {
      if (this.has(item.id)) {
        return { ok: true, removed: true, list: this.remove(item.id) };
      }
      return this.add(item);
    },

    updateVariant(id, variantId, patch = {}) {
      const list = readList();
      const entry = list.find((candidate) => candidate.id === Number(id));
      if (!entry) return;

      entry.variantId = variantId;
      Object.assign(entry, patch);
      writeList(list);
    },

    clear() {
      writeList([]);
    },
  };

  // Let listeners that mount after the store do a first sync.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { items: readList() } }));
    });
  } else {
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { items: readList() } }));
  }
})();

if (!customElements.get('compare-toggle')) {
  customElements.define(
    'compare-toggle',
    class CompareToggle extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.onClick = this.handleClick.bind(this);
        this.onStoreChange = this.syncState.bind(this);

        this.button?.addEventListener('click', this.onClick);
        document.addEventListener(window.CompareStore.eventName, this.onStoreChange);

        this.syncState();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        document.removeEventListener(window.CompareStore.eventName, this.onStoreChange);
      }

      get productId() {
        return Number(this.dataset.productId);
      }

      snapshot() {
        return {
          id: this.productId,
          variantId: this.dataset.variantId ? Number(this.dataset.variantId) : undefined,
          title: this.dataset.title || '',
          image: this.dataset.image || '',
          price: this.dataset.price || '',
          url: this.dataset.url || '',
        };
      }

      handleClick(event) {
        event.preventDefault();
        const result = window.CompareStore.toggle(this.snapshot());

        if (!result.ok && result.reason === 'limit') {
          // Open the drawer so the "limit reached" notice is actually visible to the shopper.
          const drawer = document.getElementById('CompareDrawer');
          drawer?.show?.(this.button);
          return;
        }

        // Once there are at least two products to compare, surface the drawer straight away
        // so the shopper sees the comparison is ready without hunting for the floating icon.
        if (result.ok && !result.removed && window.CompareStore.count() > 1) {
          const drawer = document.getElementById('CompareDrawer');
          drawer?.show?.(this.button);
        }
      }

      syncState() {
        const active = window.CompareStore.has(this.productId);
        this.classList.toggle('is-active', active);

        if (this.button) {
          this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
          this.button.setAttribute(
            'aria-label',
            active ? this.dataset.labelRemove || 'Remove from compare' : this.dataset.labelAdd || 'Add to compare'
          );
        }
      }

      // Called by product-info.js when the shopper picks a different variant on the PDP.
      updateVariant(variantId, patch = {}) {
        if (variantId) this.dataset.variantId = variantId;
        Object.keys(patch).forEach((key) => {
          if (patch[key] != null) this.dataset[key] = patch[key];
        });

        if (window.CompareStore.has(this.productId)) {
          window.CompareStore.updateVariant(this.productId, variantId, patch);
        }
      }
    }
  );
}

if (!customElements.get('compare-count')) {
  customElements.define(
    'compare-count',
    class CompareCount extends HTMLElement {
      connectedCallback() {
        this.onStoreChange = this.render.bind(this);
        document.addEventListener(window.CompareStore.eventName, this.onStoreChange);
        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener(window.CompareStore.eventName, this.onStoreChange);
      }

      render() {
        const count = window.CompareStore.count();
        this.textContent = count;
        this.hidden = count === 0;
        this.closest('[data-compare-floating]')?.classList.toggle('is-visible', count > 0);
      }
    }
  );
}

if (!customElements.get('compare-drawer')) {
  customElements.define(
    'compare-drawer',
    class CompareDrawer extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-compare-drawer-list]');
        this.emptyState = this.querySelector('[data-compare-drawer-empty]');
        this.limitNotice = this.querySelector('[data-compare-drawer-limit]');
        this.minNotice = this.querySelector('[data-compare-drawer-min]');

        this.onStoreChange = this.render.bind(this);
        this.onLimitReached = this.showLimitNotice.bind(this);
        this.onClick = this.handleClick.bind(this);

        document.addEventListener(window.CompareStore.eventName, this.onStoreChange);
        document.addEventListener(window.CompareStore.limitEventName, this.onLimitReached);
        this.addEventListener('click', this.onClick);

        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener(window.CompareStore.eventName, this.onStoreChange);
        document.removeEventListener(window.CompareStore.limitEventName, this.onLimitReached);
        this.removeEventListener('click', this.onClick);
      }

      handleClick(event) {
        const removeButton = event.target.closest('[data-compare-remove]');
        if (removeButton) {
          window.CompareStore.remove(removeButton.dataset.compareRemove);
          return;
        }

        if (event.target.closest('[data-compare-clear-all]')) {
          window.CompareStore.clear();
        }
      }

      showLimitNotice() {
        if (!this.limitNotice) return;
        this.limitNotice.hidden = false;
        clearTimeout(this.limitNoticeTimeout);
        this.limitNoticeTimeout = setTimeout(() => {
          this.limitNotice.hidden = true;
        }, 4000);
      }

      buildItem(item) {
        const li = document.createElement('li');
        li.className = 'compare-drawer__item';
        li.dataset.compareItem = item.id;

        const thumb = document.createElement('a');
        thumb.href = item.url || '#';
        thumb.className = 'compare-drawer__thumb';
        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.width = 60;
          img.height = 60;
          img.loading = 'lazy';
          thumb.appendChild(img);
        }

        const info = document.createElement('div');
        info.className = 'compare-drawer__item-info';

        const title = document.createElement('a');
        title.href = item.url || '#';
        title.className = 'compare-drawer__title';
        title.textContent = item.title || '';
        info.appendChild(title);

        if (item.price) {
          const price = document.createElement('span');
          price.className = 'compare-drawer__price';
          price.textContent = item.price;
          info.appendChild(price);
        }

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'compare-drawer__remove';
        remove.dataset.compareRemove = item.id;
        remove.setAttribute('aria-label', 'Remove');
        remove.innerHTML = '&times;';

        li.append(thumb, info, remove);
        return li;
      }

      render() {
        const items = window.CompareStore.getAll();
        const canCompare = items.length >= 2;

        if (this.list) {
          this.list.replaceChildren(...items.map((item) => this.buildItem(item)));
        }

        if (this.emptyState) this.emptyState.hidden = items.length > 0;
        if (this.list) this.list.hidden = items.length === 0;
        if (this.minNotice) this.minNotice.hidden = items.length !== 1;

        // Comparing needs at least two products.
        this.querySelectorAll('[data-compare-view-link]').forEach((link) => {
          link.classList.toggle('disabled', !canCompare);
          if (!canCompare) link.setAttribute('aria-disabled', 'true');
          else link.removeAttribute('aria-disabled');
        });

        this.querySelectorAll('[data-compare-clear-all]').forEach((button) => {
          button.hidden = items.length === 0;
        });
      }
    }
  );
}
